import React from 'react';
import { CHAR_PT, LINE_PT, MARGIN_LEFT_PT, MARGIN_PT, PAPER, type Page, type Paper, type TitlePage } from '../../utils/screenplay';

/** The typewriter face of a screenplay: Courier 12, ten characters an inch */
export const COURIER = "'Courier Prime', 'Courier New', Courier, monospace";

/** The page's measures in pixels at a scale (pixels a point) */
export const pageMetrics = (paper: Paper, scale: number) => ({
  width: PAPER[paper].widthPt * scale,
  height: PAPER[paper].heightPt * scale,
  lineHeight: LINE_PT * scale,
  charWidth: CHAR_PT * scale,
  fontSize: LINE_PT * scale,
  left: MARGIN_LEFT_PT * scale,
  top: MARGIN_PT * scale,
  right: MARGIN_PT * scale,
});

/** The sheet of paper every page is drawn on */
export const Sheet: React.FC<{ paper: Paper; scale: number; children?: React.ReactNode; className?: string; data?: string; onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void }> = ({ paper, scale, children, className = '', data, onPointerDown }) => {
  const m = pageMetrics(paper, scale);
  return (
    <div
      className={`relative shrink-0 bg-white text-ink border border-ink/10 shadow-soft ${className}`}
      style={{ width: m.width, height: m.height, fontFamily: COURIER, fontSize: m.fontSize, lineHeight: `${m.lineHeight}px` }}
      onPointerDown={onPointerDown}
      data-screenplay-page={data}
    >
      {children}
    </div>
  );
};

/** One page as printed, at a scale of the paper's size in points */
export const PageView: React.FC<{ page: Page; paper: Paper; scale: number }> = ({ page, paper, scale }) => {
  const m = pageMetrics(paper, scale);
  return (
    <Sheet paper={paper} scale={scale} data={String(page.number)}>
      {page.number > 1 && <span className="absolute" style={{ right: m.right, top: 36 * scale }}>{page.number}.</span>}
      <div className="absolute overflow-hidden whitespace-pre" style={{ left: m.left, top: m.top, right: m.right, bottom: m.top }}>
        {page.lines.map((line, i) => (
          <div key={i} style={{ paddingLeft: line.indent * m.charWidth, height: m.lineHeight, fontWeight: line.kind === 'scene_heading' ? 700 : 400 }}>{line.text}</div>
        ))}
      </div>
    </Sheet>
  );
};

/** The title page, drawn as the pages are */
export const TitlePageView: React.FC<{ titlePage: TitlePage; paper: Paper; scale: number }> = ({ titlePage, paper, scale }) => {
  const m = pageMetrics(paper, scale);
  return (
    <Sheet paper={paper} scale={scale} data="title" className="text-center">
      <div className="absolute inset-x-0 whitespace-pre-wrap" style={{ top: m.height / 3 }}>
        <div className="font-bold uppercase">{titlePage.title}</div>
        {titlePage.author && <div style={{ marginTop: m.lineHeight * 4 }}>{titlePage.credit || 'Written by'}</div>}
        {titlePage.author && <div style={{ marginTop: m.lineHeight * 2 }}>{titlePage.author}</div>}
      </div>
      <div className="absolute text-left whitespace-pre-wrap" style={{ left: m.left, bottom: m.top }}>{[titlePage['draft date'] || titlePage.date, titlePage.contact].filter(Boolean).join('\n')}</div>
    </Sheet>
  );
};
